import React, { ReactNode, createContext, useContext, useEffect, useRef, useState } from 'react';

import { useNotifications } from '../hooks/useNotifications';
import {
  addServiceToWishlist,
  addToWishlist,
  getWishlist,
  removeFromWishlist,
  removeServiceFromWishlist,
} from '../services/apiService';
import { WishlistItem } from '../types';
import { useAuth } from './AuthContext';

interface WishlistContextType {
  wishlistItems: WishlistItem[];
  isInWishlist: (accommodationId: number) => boolean;
  isServiceInWishlist: (serviceId: number) => boolean;
  addItemToWishlist: (accommodationId: number) => Promise<void>;
  addServiceToWishlist: (serviceId: number) => Promise<void>;
  removeItemFromWishlist: (accommodationId: number) => Promise<void>;
  removeServiceFromWishlist: (serviceId: number) => Promise<void>;
  toggleWishlistItem: (accommodationId: number) => Promise<void>;
  toggleServiceWishlistItem: (serviceId: number) => Promise<void>;
  ensureInWishlist: (item: { kind: 'accommodation' | 'service'; id: number }) => Promise<boolean>;
  loading: boolean;
  refreshWishlist: () => Promise<void>;
}

const ADD_FAILED_MESSAGE = 'Failed to add to wishlist';
const ADD_SERVICE_FAILED_MESSAGE = 'Failed to add service to wishlist';

// An expired session opens the sign-in pop-up itself, so its 401/403 needs no toast.
const isSessionExpiredError = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  return status === 401 || status === 403;
};

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

interface WishlistProviderProps {
  children: ReactNode;
}

export const WishlistProvider: React.FC<WishlistProviderProps> = ({ children }) => {
  const [wishlistItems, setWishlistItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();
  const { addNotification } = useNotifications();
  // Bumped whenever ensureInWishlist publishes a list, so a fetch that started earlier and
  // resolves later cannot overwrite it with stale items.
  const publishedVersionRef = useRef(0);

  const fetchWishlist = async () => {
    if (!isAuthenticated) {
      setWishlistItems([]);
      return;
    }

    try {
      setLoading(true);
      const versionAtStart = publishedVersionRef.current;
      const items = await getWishlist();
      if (versionAtStart === publishedVersionRef.current) setWishlistItems(items);
    } catch (error) {
      console.error('Failed to fetch wishlist:', error);
      if (!isSessionExpiredError(error)) addNotification('Failed to load wishlist', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWishlist();
  }, [isAuthenticated]);

  const isInWishlist = (accommodationId: number): boolean => {
    return wishlistItems.some((item) => item.accommodation_id === accommodationId);
  };

  const addItemToWishlist = async (accommodationId: number): Promise<void> => {
    if (!isAuthenticated) {
      addNotification('Please log in to add items to wishlist', 'info');
      return;
    }

    try {
      await addToWishlist(accommodationId);
      await fetchWishlist();
    } catch (error) {
      console.error('Failed to add to wishlist:', error);
      addNotification(ADD_FAILED_MESSAGE, 'error');
    }
  };

  const removeItemFromWishlist = async (accommodationId: number): Promise<void> => {
    if (!isAuthenticated) return;

    try {
      await removeFromWishlist(accommodationId);
      setWishlistItems((prev) => prev.filter((item) => item.accommodation_id !== accommodationId));
    } catch (error) {
      console.error('Failed to remove from wishlist:', error);
      addNotification('Failed to remove from wishlist', 'error');
    }
  };

  const toggleWishlistItem = async (accommodationId: number): Promise<void> => {
    if (isInWishlist(accommodationId)) {
      await removeItemFromWishlist(accommodationId);
    } else {
      await addItemToWishlist(accommodationId);
    }
  };

  const refreshWishlist = async (): Promise<void> => {
    await fetchWishlist();
  };

  // Service wishlist functions
  const isServiceInWishlist = (serviceId: number): boolean => {
    return wishlistItems.some((item) => item.service_id === serviceId);
  };

  const addServiceToWishlistFunc = async (serviceId: number): Promise<void> => {
    if (!isAuthenticated) {
      addNotification('Please log in to add items to wishlist', 'info');
      return;
    }

    try {
      await addServiceToWishlist(serviceId);
      await fetchWishlist();
    } catch (error) {
      console.error('Failed to add service to wishlist:', error);
      addNotification(ADD_SERVICE_FAILED_MESSAGE, 'error');
    }
  };

  const removeServiceFromWishlistFunc = async (serviceId: number): Promise<void> => {
    if (!isAuthenticated) return;

    try {
      await removeServiceFromWishlist(serviceId);
      await fetchWishlist();
    } catch (error) {
      console.error('Failed to remove service from wishlist:', error);
      addNotification('Failed to remove service from wishlist', 'error');
    }
  };

  const toggleServiceWishlistItem = async (serviceId: number): Promise<void> => {
    if (isServiceInWishlist(serviceId)) {
      await removeServiceFromWishlistFunc(serviceId);
    } else {
      await addServiceToWishlistFunc(serviceId);
    }
  };

  /**
   * Saves an item without ever removing one: reads the wishlist fresh (the provider's own
   * fetch may still be in flight right after sign-in), adds only when absent, then publishes
   * the re-read list so hearts fill. Resolves true when the item is saved afterwards.
   */
  const ensureInWishlist = async ({
    kind,
    id,
  }: {
    kind: 'accommodation' | 'service';
    id: number;
  }): Promise<boolean> => {
    const isService = kind === 'service';
    try {
      const current = await getWishlist();
      const alreadySaved = current.some((item) =>
        isService ? item.service_id === id : item.accommodation_id === id,
      );
      if (alreadySaved) {
        setWishlistItems(current);
        return true;
      }
      if (isService) {
        await addServiceToWishlist(id);
      } else {
        await addToWishlist(id);
      }
      const updated = await getWishlist();
      publishedVersionRef.current += 1;
      setWishlistItems(updated);
      return true;
    } catch (error) {
      console.error('Failed to save item to wishlist:', error);
      addNotification(isService ? ADD_SERVICE_FAILED_MESSAGE : ADD_FAILED_MESSAGE, 'error');
      return false;
    }
  };

  const value: WishlistContextType = {
    wishlistItems,
    isInWishlist,
    isServiceInWishlist,
    addItemToWishlist,
    addServiceToWishlist: addServiceToWishlistFunc,
    removeItemFromWishlist,
    removeServiceFromWishlist: removeServiceFromWishlistFunc,
    toggleWishlistItem,
    toggleServiceWishlistItem,
    ensureInWishlist,
    loading,
    refreshWishlist,
  };

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
};

export const useWishlist = (): WishlistContextType => {
  const context = useContext(WishlistContext);
  if (context === undefined) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};
